import React, { useCallback, useEffect, useMemo, useState } from "react";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExploreOutlinedIcon from "@mui/icons-material/ExploreOutlined";
import MessageOutlinedIcon from "@mui/icons-material/MessageOutlined";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import SearchIcon from "@mui/icons-material/Search";
import {
    Box,
    Card,
    Chip,
    Grid,
    IconButton,
    Paper,
    Skeleton,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { trackEvent } from "../../services/GoogleAnalytics";
import HomeService from "../../services/HomeService";
import UserService from "../../services/UserService";
import UtilService from "../../services/UtilService";
import {
    getBrowsePuzzleDisplayCategory,
    homeBrowseRowToCardPuzzle,
    isHomeBrowsePuzzlePublished,
} from "./homePuzzleRoUtils";
import { usePagesTranslation } from "../locales";
import BrowseStylePuzzleCard from "./BrowseStylePuzzleCard";
import PuzzleListingAbout from "../components/PuzzleListingAbout";
import LandingCrosswordInputSection from "../landing/LandingCrosswordInputSection";
import { TEXT } from "../../dark-theme/palette";
import BrowseAccessFilter from "../components/BrowseAccessFilter";
import BrowseTagScrollRow from "../components/BrowseTagScrollRow";
import { TagChipAvatar } from "../puzzles/common/components/TagChipAvatar";
import {
    getCrossNavigationPath,
    getGridNavigationPath,
} from "../puzzles/common/utils/PuzzleCardHelpers";

const PAGE_SIZE = 12;
/** Sentinel for the unfiltered chip; API gets no `tags` param. */
const TAG_ALL = "all";
/** Browse pages: English puzzles only (matches `/puzzles/sliding`). */
const BROWSE_CONTENT_LANGUAGE = "english";

function PuzzleCardSkeleton() {
    return (
        <Card variant="outlined" sx={{ height: "100%" }}>
            <Box sx={{ p: 2 }}>
                <Skeleton variant="rounded" height={24} sx={{ mb: 1 }} />
                <Skeleton variant="rounded" height={28} sx={{ mb: 1 }} />
                <Skeleton variant="text" />
                <Skeleton variant="rounded" height={140} sx={{ mt: 1 }} />
            </Box>
        </Card>
    );
}

function toVisibleEntries(content, browseKind, accessFilter = "all") {
    let list = Array.isArray(content) ? content : [];
    list = list.filter((row) => getBrowsePuzzleDisplayCategory(row) === browseKind);
    list = list.filter((row) => rowMatchesAccessFilter(row, accessFilter));
    return list
        .filter(isHomeBrowsePuzzlePublished)
        .map((item) => {
            const mapped = homeBrowseRowToCardPuzzle(item);
            if (!mapped?.puzzle) return null;
            return { item, puzzle: mapped.puzzle, category: mapped.category };
        })
        .filter(Boolean);
}

function tagLabelFromRow(row) {
    return typeof row?.tag === "string" ? row.tag : String(row?.tag ?? row?.name ?? "");
}

/** Chip / heading label — e.g. `animal` → `Animal`. Filter still uses the raw tag. */
function formatBrowseTagDisplayLabel(raw) {
    const s = String(raw ?? "").trim();
    if (!s) return "";
    const lower = s.toLowerCase();
    return lower.charAt(0).toUpperCase() + lower.slice(1);
}

/** Match `/home/puzzle` access tiers when API omits strict filtering client-side. */
function rowMatchesAccessFilter(row, accessFilter) {
    const af = String(accessFilter ?? "all").toLowerCase();
    if (af === "all") return true;
    const ct = row?.crossword?.crossTpl ?? row?.crossTpl;
    const gt = row?.grid?.gridTpl ?? row?.gridTpl;
    const tpl = ct || gt;
    const isFree = tpl?.free === true;
    if (af === "free") return isFree;
    if (af === "paid") return !isFree;
    return true;
}

/**
 * Tag-chip browse for crossword / word-search / hidden-message.
 * @param {{ browseKind: 'CROSSWORD' | 'WORD_SEARCH' | 'HIDDEN_MESSAGE' }} props
 */
export default function HomeBrowsePuzzlesPage({ browseKind }) {
    const { t, lang } = usePagesTranslation();
    const navigate = useNavigate();

    const [tags, setTags] = useState([]);
    const [tagsLoading, setTagsLoading] = useState(true);
    const [selectedTag, setSelectedTag] = useState(TAG_ALL);
    const [accessFilter, setAccessFilter] = useState("all");
    /** WORD_SEARCH only: "all" | "mini" (6×6–9×9) | "12" (classic 12×12). */
    const [sizeFilter, setSizeFilter] = useState("all");
    const [page, setPage] = useState(0);
    const [entries, setEntries] = useState([]);
    const [totalPages, setTotalPages] = useState(0);
    const [listLoading, setListLoading] = useState(true);

    const [userPayPlan, setUserPayPlan] = useState(null);
    const [userPlanResolved, setUserPlanResolved] = useState(false);
    const [subscriptionStatus, setSubscriptionStatus] = useState(null);

    const contentLanguage = BROWSE_CONTENT_LANGUAGE;

    const pageTitleKey = useMemo(() => {
        if (browseKind === "CROSSWORD") return "puzzles-browse.page-title-crossword";
        if (browseKind === "WORD_SEARCH") return "puzzles-browse.page-title-word-search";
        if (browseKind === "HIDDEN_MESSAGE") return "puzzles-browse.page-title-hidden-message";
        return "puzzles-browse.page-title";
    }, [browseKind]);

    const pageSubtitleKey = useMemo(() => {
        if (browseKind === "CROSSWORD") return "puzzles-browse.page-subtitle-crossword";
        if (browseKind === "WORD_SEARCH") return "puzzles-browse.page-subtitle-word-search";
        if (browseKind === "HIDDEN_MESSAGE") return "puzzles-browse.page-subtitle-hidden-message";
        return "puzzles-browse.page-subtitle";
    }, [browseKind]);

    useEffect(() => {
        UserService.getUserInfo()
            .then((data) => {
                if (data?.cloudUser) {
                    setUserPayPlan(UtilService.resolveWebPayPlan(data.cloudUser));
                    setSubscriptionStatus(data.cloudUser.currSub?.status || null);
                }
            })
            .catch(() => {})
            .finally(() => setUserPlanResolved(true));
    }, []);

    useEffect(() => {
        trackEvent("entered", { name: "HomeBrowsePuzzlesPage", browseKind });
    }, [browseKind]);

    useEffect(() => {
        setSelectedTag(TAG_ALL);
        setAccessFilter("all");
        setSizeFilter("all");
        setPage(0);
        setEntries([]);
        setTotalPages(0);
    }, [browseKind]);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    }, [page, selectedTag]);

    useEffect(() => {
        if (!userPlanResolved) {
            setTagsLoading(true);
            return undefined;
        }
        let cancel = false;
        setTagsLoading(true);
        HomeService.getHomePuzzleDistinctTags(contentLanguage, browseKind, accessFilter)
            .then((list) => {
                if (!cancel) setTags(Array.isArray(list) ? list : []);
            })
            .catch(() => {
                if (!cancel) setTags([]);
            })
            .finally(() => {
                if (!cancel) setTagsLoading(false);
            });
        return () => {
            cancel = true;
        };
    }, [contentLanguage, browseKind, accessFilter, userPlanResolved]);

    useEffect(() => {
        if (!userPlanResolved) return undefined;
        let cancel = false;
        setListLoading(true);
        const tagFilter = selectedTag === TAG_ALL ? [] : [selectedTag];
        HomeService.getHomePuzzlePage({
            page,
            pageSize: PAGE_SIZE,
            language: contentLanguage,
            tags: tagFilter,
            kind: browseKind,
            accessFilter,
        })
            .then((res) => {
                if (cancel) return;
                setEntries(toVisibleEntries(res.content, browseKind, accessFilter));
                const tp = typeof res.totalPages === "number" ? res.totalPages : 0;
                setTotalPages(tp > 0 ? tp : 0);
            })
            .catch(() => {
                if (cancel) return;
                setEntries([]);
                setTotalPages(0);
            })
            .finally(() => {
                if (!cancel) setListLoading(false);
            });
        return () => {
            cancel = true;
        };
    }, [
        userPlanResolved,
        page,
        selectedTag,
        contentLanguage,
        browseKind,
        accessFilter,
    ]);

    const handleSelectTag = useCallback((tag) => {
        setSelectedTag(tag);
        setPage(0);
    }, []);

    const handleAccessFilter = useCallback((next) => {
        setAccessFilter(next);
        setPage(0);
    }, []);

    const handlePlayClick = useCallback(
        (p) => {
            const path =
                p?.type === "CROSSWORD" ? getCrossNavigationPath(p) : getGridNavigationPath(p);
            if (path) navigate(path);
        },
        [navigate]
    );

    const visibleEntries = useMemo(() => {
        if (browseKind !== "WORD_SEARCH" || sizeFilter === "all") return entries;
        return entries.filter(({ puzzle }) => {
            const tpl = puzzle?.template;
            const r = Number(tpl?.gridRows ?? tpl?.grid_rows) || 12;
            const c = Number(tpl?.gridCols ?? tpl?.grid_cols) || 12;
            const classic = r === 12 && c === 12;
            if (sizeFilter === "mini") return !classic;
            if (sizeFilter === "12") return classic;
            return true;
        });
    }, [entries, browseKind, sizeFilter]);

    const canGoPrev = page > 0 && !listLoading;
    const canGoNext = totalPages > 0 && page < totalPages - 1 && !listLoading;
    const showPager = totalPages > 1;

    const completelyEmpty =
        userPlanResolved && !tagsLoading && !listLoading && visibleEntries.length === 0;

    const heroStyle = useMemo(() => {
        if (browseKind === "CROSSWORD") {
            return {
                background:
                    "linear-gradient(125deg, #0d47a1 0%, #1565c0 28%, #1976d2 58%, #2761F5 82%, #5c7cfa 100%)",
                boxShadow: "0 8px 32px rgba(39, 97, 245, 0.28)",
                HeroIcon: ExploreOutlinedIcon,
            };
        }
        if (browseKind === "WORD_SEARCH") {
            return {
                background:
                    "linear-gradient(125deg, #0d3d1a 0%, #1b5e20 32%, #2e7d32 62%, #388e3c 100%)",
                boxShadow: "0 8px 32px rgba(27, 94, 32, 0.28)",
                HeroIcon: SearchIcon,
            };
        }
        if (browseKind === "HIDDEN_MESSAGE") {
            return {
                background:
                    "linear-gradient(125deg, #0d47a1 0%, #1565c0 28%, #1976d2 58%, #1e88e5 82%, #42a5f5 100%)",
                boxShadow: "0 8px 32px rgba(21, 101, 192, 0.28)",
                HeroIcon: MessageOutlinedIcon,
            };
        }
        return {
            background:
                "linear-gradient(125deg, #38006b 0%, #4a148c 28%, #6a1b9a 58%, #7b1fa2 82%, #8e24aa 100%)",
            boxShadow: "0 8px 32px rgba(74, 20, 140, 0.28)",
            HeroIcon: ExploreOutlinedIcon,
        };
    }, [browseKind]);

    const HeroIcon = heroStyle.HeroIcon;

    const tagChipRows = useMemo(
        () =>
            tags
                .map((row) => {
                    const lbl = tagLabelFromRow(row);
                    if (!lbl) return null;
                    return { raw: lbl, label: formatBrowseTagDisplayLabel(lbl) };
                })
                .filter(Boolean),
        [tags]
    );

    return (
        <Box id={browseKind === "CROSSWORD" ? "crossword-catalog" : undefined} sx={{ flexGrow: 1, p: 3 }}>
            <Paper
                elevation={2}
                sx={{
                    mb: 3,
                    p: { xs: 2.5, sm: 3 },
                    borderRadius: 2,
                    background: heroStyle.background,
                    color: "#fff",
                    position: "relative",
                    overflow: "hidden",
                    boxShadow: heroStyle.boxShadow,
                }}
            >
                <HeroIcon
                    sx={{
                        position: "absolute",
                        right: 16,
                        top: "50%",
                        transform: "translateY(-50%)",
                        fontSize: 120,
                        color: "rgba(255,255,255,0.14)",
                        pointerEvents: "none",
                    }}
                    aria-hidden
                />
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5, position: "relative", zIndex: 1 }}>
                    {t(pageTitleKey)}
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.95, position: "relative", zIndex: 1 }}>
                    {t(pageSubtitleKey)}
                </Typography>
                <Typography
                    variant="body2"
                    sx={{
                        mt: 1.25,
                        opacity: 0.95,
                        position: "relative",
                        zIndex: 1,
                        display: "flex",
                        alignItems: "center",
                        gap: 0.75,
                        fontWeight: 600,
                    }}
                >
                    <PictureAsPdfIcon sx={{ fontSize: 20, flexShrink: 0 }} aria-hidden />
                    {t(
                        "puzzles-browse.pdf-offline-note",
                        "Download PDF is available — print it and play offline.",
                    )}
                </Typography>
            </Paper>

            <BrowseAccessFilter value={accessFilter} onChange={handleAccessFilter} t={t} />

            {browseKind === "WORD_SEARCH" ? (
                <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1.5 }}>
                    <ToggleButtonGroup
                        value={sizeFilter}
                        exclusive
                        size="small"
                        onChange={(_, next) => {
                            if (next != null) setSizeFilter(next);
                        }}
                        aria-label={t("puzzles-browse.filter-size-label", "Grid size")}
                        sx={{
                            "& .MuiToggleButton-root": {
                                px: 1.5,
                                minHeight: 44,
                                textTransform: "none",
                                "&.Mui-selected": {
                                    backgroundColor: "#3BA666",
                                    color: "white",
                                    "&:hover": { backgroundColor: "#2e7d32" },
                                },
                            },
                        }}
                    >
                        <ToggleButton value="all">{t("puzzles-browse.filter-size-all", "6×6–12×12")}</ToggleButton>
                        <ToggleButton value="mini">
                            {t("puzzles-browse.filter-size-mini", "Mini · 6×6–9×9")}
                        </ToggleButton>
                        <ToggleButton value="12">
                            {t("puzzles-browse.filter-size-classic", "Classic · 12×12")}
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Box>
            ) : null}

            <BrowseTagScrollRow
                ariaLabel={t("puzzles-browse.filters-tags", "Tags")}
                scrollLeftLabel={t("puzzle.tags.scroll-left", "Scroll left")}
                scrollRightLabel={t("puzzle.tags.scroll-right", "Scroll right")}
            >
                {tagsLoading ? (
                    <>
                        <Skeleton variant="rounded" width={64} height={32} sx={{ borderRadius: 16, flexShrink: 0 }} />
                        <Skeleton variant="rounded" width={88} height={32} sx={{ borderRadius: 16, flexShrink: 0 }} />
                        <Skeleton variant="rounded" width={96} height={32} sx={{ borderRadius: 16, flexShrink: 0 }} />
                        <Skeleton variant="rounded" width={72} height={32} sx={{ borderRadius: 16, flexShrink: 0 }} />
                    </>
                ) : (
                    <>
                        <Chip
                            label={t("puzzles-browse.tag-all", "All")}
                            clickable
                            onClick={() => handleSelectTag(TAG_ALL)}
                            color={selectedTag === TAG_ALL ? "primary" : "default"}
                            variant={selectedTag === TAG_ALL ? "filled" : "outlined"}
                            sx={{ minHeight: 44, px: 0.5, flexShrink: 0 }}
                        />
                        {tagChipRows.map(({ raw, label }) => {
                            const selected = selectedTag === raw;
                            return (
                                <Chip
                                    key={raw}
                                    avatar={<TagChipAvatar tag={raw} size={18} />}
                                    label={label}
                                    clickable
                                    onClick={() => handleSelectTag(raw)}
                                    color={selected ? "primary" : "default"}
                                    variant={selected ? "filled" : "outlined"}
                                    sx={{ minHeight: 44, px: 0.5, flexShrink: 0 }}
                                />
                            );
                        })}
                    </>
                )}
            </BrowseTagScrollRow>

            {listLoading ? (
                <Grid container spacing={2}>
                    {Array.from({ length: 6 }).map((_, i) => (
                        <Grid key={`sk-${i}`} size={{ xs: 12, sm: 6, md: 4 }}>
                            <PuzzleCardSkeleton />
                        </Grid>
                    ))}
                </Grid>
            ) : visibleEntries.length > 0 ? (
                <Grid container spacing={2}>
                    {visibleEntries.map(({ item, puzzle, category }) => (
                        <Grid
                            key={`${category}-${puzzle.id}`}
                            size={{ xs: 12, sm: 6, md: 4 }}
                        >
                            <BrowseStylePuzzleCard
                                category={category}
                                puzzle={puzzle}
                                storyTeaserRow={item}
                                contentLanguage={contentLanguage}
                                lang={lang}
                                t={t}
                                userPayPlan={userPayPlan}
                                subscriptionStatus={subscriptionStatus}
                                onPlayClick={handlePlayClick}
                            />
                        </Grid>
                    ))}
                </Grid>
            ) : completelyEmpty ? (
                <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
                    {t("puzzles-browse.empty")}
                </Typography>
            ) : null}

            {showPager ? (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1,
                        mt: 3,
                        mb: 1,
                    }}
                >
                    <IconButton
                        onClick={() => setPage((p) => Math.max(0, p - 1))}
                        disabled={!canGoPrev}
                        aria-label={t("puzzles-browse.page-previous", "Previous")}
                        size="large"
                        sx={{ minWidth: 44, minHeight: 44 }}
                    >
                        <ChevronLeftIcon />
                    </IconButton>
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 72, textAlign: "center" }}>
                        {page + 1} / {totalPages}
                    </Typography>
                    <IconButton
                        onClick={() => setPage((p) => p + 1)}
                        disabled={!canGoNext}
                        aria-label={t("puzzles-browse.page-next", "Next")}
                        size="large"
                        sx={{ minWidth: 44, minHeight: 44 }}
                    >
                        <ChevronRightIcon />
                    </IconButton>
                </Box>
            ) : null}
            {browseKind === "CROSSWORD" ? (
                <Box
                    sx={{
                        mt: { xs: 4, sm: 5 },
                        pt: { xs: 3, sm: 3.5 },
                        borderTop: `1px solid ${TEXT.disabled}33`,
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) minmax(0, 1fr)" },
                        gap: { xs: 3, md: 4 },
                        alignItems: "start",
                    }}
                >
                    <PuzzleListingAbout puzzleKind="CROSSWORD" flush />
                    <LandingCrosswordInputSection />
                </Box>
            ) : (
                <PuzzleListingAbout puzzleKind={browseKind} />
            )}
        </Box>
    );
}

HomeBrowsePuzzlesPage.propTypes = {
    browseKind: PropTypes.oneOf(["CROSSWORD", "WORD_SEARCH", "HIDDEN_MESSAGE"]).isRequired,
};
